import React, { useState, useEffect } from "react";
import { QRCodeSVG } from "qrcode.react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import api from "@/lib/api";
import { Shield, ShieldCheck, ShieldOff, Copy, Download, KeyRound } from "lucide-react";

export default function SecurityTab({ onStatusChange }) {
  const [enabled, setEnabled] = useState(null);
  const [loading, setLoading] = useState(false);
  // Setup dialog state
  const [setupOpen, setSetupOpen] = useState(false);
  const [setupStep, setSetupStep] = useState("scan"); // "scan" | "verify" | "backup"
  const [setupData, setSetupData] = useState(null); // { secret, otpauth_uri, backup_codes }
  const [setupCode, setSetupCode] = useState("");
  const [busy, setBusy] = useState(false);
  // Disable dialog state
  const [disableOpen, setDisableOpen] = useState(false);
  const [disablePw, setDisablePw] = useState("");
  const [disableCode, setDisableCode] = useState("");

  const loadStatus = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/auth/2fa/status");
      setEnabled(!!data.enabled);
      onStatusChange?.(!!data.enabled);
    } catch (e) {
      toast.error("Failed to load 2FA status");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { loadStatus(); }, []);

  const startSetup = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/auth/2fa/setup-init");
      setSetupData(data);
      setSetupStep("scan");
      setSetupCode("");
      setSetupOpen(true);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not initialise 2FA setup");
    } finally {
      setBusy(false);
    }
  };

  const confirmSetup = async () => {
    if (!setupCode || setupCode.replace(/\D/g, "").length < 6) {
      toast.error("Enter the 6-digit code from your authenticator app");
      return;
    }
    setBusy(true);
    try {
      await api.post("/auth/2fa/setup-confirm", {
        secret: setupData.secret,
        code: setupCode.trim(),
        backup_codes: setupData.backup_codes,
      });
      setSetupStep("backup");
      toast.success("Two-factor authentication is now active");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Invalid code — try the current one from your app");
    } finally {
      setBusy(false);
    }
  };

  const finishSetup = () => {
    setSetupOpen(false);
    setSetupData(null);
    setSetupCode("");
    setSetupStep("scan");
    loadStatus();
  };

  const doDisable = async () => {
    if (!disablePw || !disableCode) {
      toast.error("Enter your password and current authenticator code");
      return;
    }
    setBusy(true);
    try {
      await api.post("/auth/2fa/disable", {
        current_password: disablePw,
        code: disableCode.trim(),
      });
      toast.success("Two-factor authentication disabled");
      setDisableOpen(false);
      setDisablePw("");
      setDisableCode("");
      loadStatus();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not disable 2FA");
    } finally {
      setBusy(false);
    }
  };

  const copyBackupCodes = () => {
    const text = (setupData?.backup_codes || []).join("\n");
    navigator.clipboard.writeText(text).then(() => toast.success("Backup codes copied"));
  };

  const downloadBackupCodes = () => {
    const text = [
      "KELTA — Two-Factor Backup Codes",
      "Keep these somewhere safe. Each code works once.",
      "Generated: " + new Date().toLocaleString(),
      "",
      ...(setupData?.backup_codes || []),
    ].join("\n");
    const blob = new Blob([text], { type: "text/plain" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "kelta-2fa-backup-codes.txt";
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-5">
      <Card className="p-6" data-testid="security-2fa-card">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="flex items-start gap-4">
            <div className={"w-12 h-12 rounded-lg flex items-center justify-center " + (enabled ? "bg-emerald-500/10 text-emerald-600" : "bg-amber-500/10 text-amber-600")}>
              {enabled ? <ShieldCheck className="w-6 h-6" /> : <Shield className="w-6 h-6" />}
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h3 className="text-lg font-bold">Two-Factor Authentication</h3>
                {enabled === true && <Badge className="bg-emerald-500 hover:bg-emerald-500 text-white">Active</Badge>}
                {enabled === false && <Badge variant="outline">Not set up</Badge>}
                {enabled === null && <Badge variant="outline">…</Badge>}
              </div>
              <p className="text-sm text-muted-foreground mt-1 max-w-xl">
                Protect the super admin account with a rotating 6-digit code from Google Authenticator, Authy or any TOTP app. A leaked password alone will no longer be enough to access finance data.
              </p>
            </div>
          </div>
          <div className="flex gap-2">
            {enabled ? (
              <Button
                variant="outline"
                onClick={() => setDisableOpen(true)}
                data-testid="security-2fa-disable-btn"
              >
                <ShieldOff className="w-4 h-4 mr-1.5" /> Disable
              </Button>
            ) : (
              <Button
                onClick={startSetup}
                disabled={busy || loading}
                className="bg-brand-blue hover:bg-brand-blue/90 text-white"
                data-testid="security-2fa-enable-btn"
              >
                <ShieldCheck className="w-4 h-4 mr-1.5" /> Enable 2FA
              </Button>
            )}
          </div>
        </div>
      </Card>

      {/* Setup Dialog */}
      <Dialog open={setupOpen} onOpenChange={(v) => { if (!busy) { if (!v) finishSetup(); else setSetupOpen(v); } }}>
        <DialogContent className="sm:max-w-lg" data-testid="security-2fa-setup-dialog">
          <DialogHeader>
            <DialogTitle>
              {setupStep === "scan" && "Scan the QR code"}
              {setupStep === "verify" && "Verify your app"}
              {setupStep === "backup" && "Save your backup codes"}
            </DialogTitle>
            <DialogDescription>
              {setupStep === "scan" && "Open Google Authenticator, Authy or any TOTP app and scan this code. Then click Continue."}
              {setupStep === "verify" && "Enter the current 6-digit code shown in your authenticator app to confirm setup."}
              {setupStep === "backup" && "You&apos;ll need one of these codes if you lose your device. Each code works once. Store them somewhere safe now — we won&apos;t show them again."}
            </DialogDescription>
          </DialogHeader>

          {setupStep === "scan" && setupData && (
            <div className="space-y-4">
              <div className="flex justify-center bg-white p-4 rounded-lg border">
                <QRCodeSVG value={setupData.otpauth_uri} size={196} level="M" />
              </div>
              <div className="text-xs text-muted-foreground text-center">
                Can&apos;t scan? Enter this secret manually:
                <div className="mt-1 font-mono text-sm font-bold text-foreground select-all break-all">{setupData.secret}</div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={finishSetup} data-testid="security-2fa-setup-cancel">Cancel</Button>
                <Button onClick={() => setSetupStep("verify")} data-testid="security-2fa-setup-continue">Continue</Button>
              </DialogFooter>
            </div>
          )}

          {setupStep === "verify" && (
            <div className="space-y-4">
              <div>
                <Label htmlFor="setup-code">6-digit code</Label>
                <Input
                  id="setup-code"
                  value={setupCode}
                  onChange={e => setSetupCode(e.target.value)}
                  placeholder="123 456"
                  autoFocus
                  inputMode="numeric"
                  maxLength={6}
                  className="tracking-widest text-center text-2xl font-mono h-14"
                  data-testid="security-2fa-setup-code-input"
                />
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setSetupStep("scan")} disabled={busy}>Back</Button>
                <Button onClick={confirmSetup} disabled={busy || !setupCode} data-testid="security-2fa-setup-verify-btn">
                  {busy ? "Verifying…" : "Verify & activate"}
                </Button>
              </DialogFooter>
            </div>
          )}

          {setupStep === "backup" && setupData && (
            <div className="space-y-4">
              <div className="bg-amber-500/10 border border-amber-500/40 rounded-md p-3 text-xs text-amber-900 dark:text-amber-100 flex items-start gap-2">
                <KeyRound className="w-4 h-4 mt-0.5 shrink-0" />
                <div>Each code can be used once, in place of your 6-digit code, if you ever lose your device. Once you close this dialog, we won&apos;t show them again.</div>
              </div>
              <div className="grid grid-cols-2 gap-2 font-mono text-sm">
                {setupData.backup_codes.map((c, i) => (
                  <div key={c} className="bg-muted/40 border rounded-md px-3 py-2 select-all" data-testid={`security-2fa-backup-${i}`}>
                    {c}
                  </div>
                ))}
              </div>
              <div className="flex gap-2 justify-center">
                <Button variant="outline" size="sm" onClick={copyBackupCodes} data-testid="security-2fa-copy-backup">
                  <Copy className="w-3.5 h-3.5 mr-1.5" /> Copy
                </Button>
                <Button variant="outline" size="sm" onClick={downloadBackupCodes} data-testid="security-2fa-download-backup">
                  <Download className="w-3.5 h-3.5 mr-1.5" /> Download .txt
                </Button>
              </div>
              <DialogFooter>
                <Button onClick={finishSetup} data-testid="security-2fa-setup-done-btn" className="bg-brand-blue hover:bg-brand-blue/90 text-white">
                  I&apos;ve saved these — finish
                </Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Disable Dialog */}
      <Dialog open={disableOpen} onOpenChange={setDisableOpen}>
        <DialogContent className="sm:max-w-md" data-testid="security-2fa-disable-dialog">
          <DialogHeader>
            <DialogTitle>Disable two-factor authentication?</DialogTitle>
            <DialogDescription>
              This will remove the extra security layer from your account. Enter your password and current 6-digit code to confirm.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label htmlFor="disable-pw">Current password</Label>
              <Input
                id="disable-pw"
                type="password"
                value={disablePw}
                onChange={e => setDisablePw(e.target.value)}
                data-testid="security-2fa-disable-pw"
              />
            </div>
            <div>
              <Label htmlFor="disable-code">Current 6-digit code</Label>
              <Input
                id="disable-code"
                value={disableCode}
                onChange={e => setDisableCode(e.target.value)}
                placeholder="123 456"
                inputMode="numeric"
                maxLength={6}
                className="tracking-widest font-mono text-center"
                data-testid="security-2fa-disable-code"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDisableOpen(false)}>Cancel</Button>
            <Button
              onClick={doDisable}
              disabled={busy}
              variant="destructive"
              data-testid="security-2fa-disable-confirm"
            >
              {busy ? "Disabling…" : "Disable 2FA"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
